import { AuctionStatus } from '@/types/LiveAuctionState';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getCatalogStatus } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCountDown } from '@/redux/modules/bidCountDown';
import {
    getItemSummaryBidderWonItem,
    getItemSummaryBuyNowPrice,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryHighBidEstimate,
    getItemSummaryIndex,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryIsTimedAuction,
    getItemSummaryLeadingBid,
    getItemSummaryLowBidEstimate,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getLiveCatalogCurrentItemIndex,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBid,
} from '@/redux/modules/console';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { LiveBidConsoleItem } from '@liveauctioneers/hammer-ui-liveauctioneers/desktopLiveBidPanel';
import { LotStatus } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { useBidCountdown } from '@/hooks/useBidCountdown';
import { useMemo } from 'react';

type Props = {
    itemId: number;
};

export const useGetBiddingConsoleItemData = ({ itemId }: Props): LiveBidConsoleItem => {
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemSummaryHighBidEstimate(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const bidderHasWon = useAppSelector((state) => getItemSummaryBidderWonItem(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId)) || 0;
    const lowBidEstimate = useAppSelector((state) => getItemSummaryLowBidEstimate(state, itemId));
    const soldPrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startingPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId)) || 0;

    const liveItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));

    const liveItemLeadingBid = useAppSelector((state) => getLiveItemDataLeadingBid(state, itemId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isLiveItem = liveItemIndex === index && catalogStatus === 'live';

    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemSummaryBuyNowPrice(state, itemId));
    const buyItNowAvailable =
        buyNowStatus === BuyNowStatus.Available &&
        (catalogStatus === AuctionStatus.Done || catalogStatus === undefined);

    // shouldShowTimer logic - only show during last 5 seconds of countdown
    const isTimedAuction = useAppSelector((state) => getItemSummaryIsTimedAuction(state, itemId));
    const timeStamp = useAppSelector((state) => getCountDown(state, itemId));
    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const { isLastFiveSeconds } = useBidCountdown(timeStamp);
    const shouldShowTimer = isTimedAuction && Boolean(timeStamp) && catalogRegistration.approved && isLastFiveSeconds;

    const currentPrice = useMemo(() => {
        const safeSoldOrStartingPrice = soldPrice > 0 ? soldPrice : startingPrice;

        if (buyItNowAvailable && buyNowPrice) {
            return buyNowPrice;
        }

        if (isLiveItem) {
            return liveItemLeadingBid || leadingBid || safeSoldOrStartingPrice;
        }

        return leadingBid || safeSoldOrStartingPrice;
    }, [buyItNowAvailable, buyNowPrice, isLiveItem, liveItemLeadingBid, leadingBid, soldPrice, startingPrice]);

    const lotStatus = useMemo((): LiveBidConsoleItem['lotStatus'] => {
        // Compute state conditions in priority order
        const isPassedState = isPassed;
        const isWonState = bidderHasWon && isSold && !isPassed;
        const isUnsoldState = isLocked && !isPassed && !isSold && !isLotOpen;

        // Determine lot status based on priority: passed > won > unsold > sold > live > closed
        switch (true) {
            case isPassedState:
                return LotStatus.Passed;
            case isWonState:
                return LotStatus.Won;
            case isUnsoldState:
                return LotStatus.Closed;
            case isSold:
                return LotStatus.Sold;
            case isLiveItem:
                return LotStatus.Live;
            default:
                return LotStatus.Closed;
        }
    }, [isPassed, bidderHasWon, isSold, isLocked, isLotOpen, isLiveItem]);

    const nextBidAmount = lotStatus === LotStatus.Live ? currentAskPrice : currentPrice;

    return useMemo(
        () => ({
            currentPrice,
            highBidEstimate: highBidEstimate ?? 0,
            lotStatus,
            lowBidEstimate: lowBidEstimate ?? 0,
            nextBidAmount,
            shouldShowTimer,
        }),
        [currentPrice, highBidEstimate, lotStatus, lowBidEstimate, nextBidAmount, shouldShowTimer]
    );
};
